import React, { useEffect, useMemo, useState } from 'react';
import { FiSearch, FiX } from 'react-icons/fi';
import { usePatientDoctorContext } from '../PatientDoctorContext';
import { INPUT } from '../ui/kit';
import { searchText } from './patientModel';

// Chooses one or several patient files (stores their ids). Used to attach a document
// (staff, echo, report…) to the patients it concerns.
// single: one file at most (choosing another one replaces it).
// filter(patient): limits the files offered (e.g. to the unit of the account).
const PatientPicker = ({ id, values = [], onChange, single = false, onPick, filter, placeholder = 'Rechercher un patient par nom ou n° de dossier…' }) => {
  const { patients, ensurePatients } = usePatientDoctorContext();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const listId = `${id || 'patient-picker'}-results`;

  useEffect(() => {
    ensurePatients().catch(() => {});
  }, [ensurePatients]);

  const byId = useMemo(() => Object.fromEntries(patients.map((patient) => [patient._id, patient])), [patients]);
  const query = search.trim().toLowerCase();
  const matches = useMemo(
    () =>
      query
        ? patients
            .filter((patient) => !values.includes(patient._id) && (!filter || filter(patient)) && searchText(patient).includes(query))
            .slice(0, 8)
        : [],
    [patients, query, values, filter]
  );

  const add = (patient) => {
    onChange(single ? [patient._id] : [...values, patient._id]);
    if (onPick) onPick(patient);
    setSearch('');
  };

  return (
    <div>
      {values.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {values.map((patientId) => {
            const patient = byId[patientId];
            return (
              <span key={patientId} className="inline-flex h-8 items-center gap-1.5 rounded-full bg-brand-50 pl-[12px] pr-1 text-[13px] font-medium text-brand-700">
                {patient ? patient.nom : 'Dossier introuvable'}
                {patient && patient.dossierNumber && <span className="font-normal text-brand-700/60">· {patient.dossierNumber}</span>}
                <button
                  type="button"
                  onClick={() => onChange(values.filter((item) => item !== patientId))}
                  aria-label={`Retirer ${patient ? patient.nom : 'ce dossier'}`}
                  className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border-0 bg-[transparent] text-brand-700/70 transition hover:bg-brand-100 hover:text-brand-700"
                >
                  <FiX size={13} aria-hidden="true" />
                </button>
              </span>
            );
          })}
        </div>
      )}
      <div className="relative">
        <FiSearch size={16} aria-hidden="true" className="pointer-events-none absolute left-[14px] top-1/2 -translate-y-1/2 text-black/35" />
        <input
          id={id}
          type="search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (matches[0]) add(matches[0]);
            }
          }}
          placeholder={placeholder}
          role="combobox"
          aria-controls={listId}
          aria-expanded={open && matches.length > 0}
          aria-autocomplete="list"
          className={`${INPUT} h-11 pl-[40px]`}
        />
        {open && matches.length > 0 && (
          <ul
            id={listId}
            role="listbox"
            className="absolute left-0 right-0 top-[calc(100%+6px)] z-20 m-0 max-h-[280px] list-none overflow-y-auto rounded-2xl bg-white p-1 shadow-[0_12px_40px_rgba(0,0,0,0.16)] ring-1 ring-black/[0.06]"
          >
            {matches.map((patient) => (
              <li key={patient._id} role="option" aria-selected="false">
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => add(patient)}
                  className="flex w-full cursor-pointer items-center justify-between gap-2 rounded-xl border-0 bg-[transparent] px-[12px] py-2 text-left text-[14px] text-ink transition hover:bg-black/[0.04]"
                >
                  <span className="truncate font-medium">{patient.nom}</span>
                  <span className="flex-none text-[12.5px] text-black/40">
                    {[patient.dossierNumber, patient.services].filter(Boolean).join(' · ')}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {open && query && matches.length === 0 && (
          <p className="absolute left-0 right-0 top-[calc(100%+6px)] z-20 m-0 rounded-2xl bg-white px-[14px] py-[12px] text-[13.5px] text-black/45 shadow-[0_12px_40px_rgba(0,0,0,0.16)] ring-1 ring-black/[0.06]">
            Aucun dossier ne correspond.
          </p>
        )}
      </div>
    </div>
  );
};

export default PatientPicker;
